import { useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { HorizontalScrollIndicator } from '@/components/ui/HorizontalScrollIndicator';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly end: boolean;
}

interface NavGroup {
  readonly label: string | null;
  readonly items: readonly NavItem[];
}

const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: null,
    items: [{ to: '/admin', label: 'Dashboard', end: true }],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/queue', label: 'Print Queue', end: false },
      { to: '/admin/orders', label: 'Orders', end: false },
      { to: '/admin/quotes', label: 'Quotes', end: false },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/admin/products', label: 'Products', end: false },
      { to: '/admin/materials', label: 'Materials', end: false },
      { to: '/admin/coupons', label: 'Coupons', end: false },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/admin/analytics', label: 'Analytics', end: false },
      { to: '/admin/exports', label: 'Exports', end: false },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/webhook-errors', label: 'Webhooks', end: false },
      { to: '/admin/settings', label: 'Settings', end: false },
    ],
  },
];

const FLAT_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

interface AdminSidebarProps {
  readonly variant: 'sidebar' | 'tabs';
}

export const AdminSidebar = ({ variant }: AdminSidebarProps) => {
  const scrollRef = useRef<HTMLElement>(null);

  if (variant === 'tabs') {
    return (
      <div>
        <nav
          ref={scrollRef}
          className="hide-native-scrollbar"
          style={{
            backgroundColor: 'var(--bg-surface)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div
            className="flex"
            style={{
              padding: '0 var(--space-3)',
              gap: 0,
              minWidth: 'min-content',
            }}
          >
            {FLAT_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: isActive ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)',
                  color: isActive ? 'var(--text-accent)' : 'var(--text-tertiary)',
                  borderBottom: isActive ? '2px solid var(--color-primary-600)' : '2px solid transparent',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  minHeight: '44px',
                  transition: 'all var(--duration-fast) var(--ease-out)',
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
        <HorizontalScrollIndicator scrollRef={scrollRef} />
      </div>
    );
  }

  return (
    <nav
      style={{
        width: 'var(--admin-sidebar-width)',
        minHeight: '100%',
        backgroundColor: 'var(--admin-sidebar-bg)',
        borderRight: '1px solid var(--admin-sidebar-divider)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Navigation groups */}
      <div style={{ flex: 1, padding: 'var(--space-2) 0' }}>
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi}>
            {group.label && (
              <p
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 'var(--font-weight-semibold)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--admin-sidebar-section)',
                  padding: 'var(--space-3) var(--space-4) var(--space-1)',
                  margin: 0,
                }}
              >
                {group.label}
              </p>
            )}
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className="admin-sidebar-link"
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  padding: '5px var(--space-4)',
                  margin: '1px var(--space-2)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '13px',
                  fontWeight: isActive ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)',
                  color: isActive ? 'var(--admin-sidebar-text-active)' : 'var(--admin-sidebar-text)',
                  backgroundColor: isActive ? 'var(--admin-sidebar-bg-active)' : 'transparent',
                  textDecoration: 'none',
                  transition: 'all var(--duration-fast) var(--ease-out)',
                  lineHeight: '1.4',
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
};
